import React from 'react';

import { formatAmount, formatCount, IN_PROGRESS_STATUSES, statusBadge } from './uploadFormat';

import Alert from 'CommonComponents/Alert';
import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

function Figure({ label, value }) {
    return (
        <div className="tw-rounded-lg tw-border tw-border-charcoal-15 tw-px-4 tw-py-3">
            <div className="tw-text-xs tw-text-charcoal-75">{label}</div>
            <div className="tw-mt-1 tw-text-xl tw-font-semibold tw-text-charcoal">{formatCount(value)}</div>
        </div>
    );
}

function Movement({ label, before, after, format }) {
    const change = (Number(after) || 0) - (Number(before) || 0);

    return (
        <div className="tw-rounded-lg tw-border tw-border-charcoal-15 tw-px-4 tw-py-3">
            <div className="tw-text-xs tw-text-charcoal-75">{label}</div>
            <div className="tw-mt-1 tw-text-sm tw-text-charcoal">
                {format(before)} <span aria-hidden="true">&rarr;</span> <span className="tw-font-semibold">{format(after)}</span>
            </div>
            <div className="tw-text-xs tw-text-charcoal-50">
                {change >= 0 ? $t('Up {{change}}', { change: format(change) }) : $t('Down {{change}}', { change: format(Math.abs(change)) })}
            </div>
        </div>
    );
}

function reconciliationChangeLabel(change) {
    return change === 'budget_line_moved' ? $t('Match moved to the new budget line') : $t('Amount changed, now unreconciled');
}

/**
 * Step 4. Progress while the batch is written in the background, then what it
 * did. The user may leave: an email follows when it finishes.
 */
export default function UploadStatus({ batch, historyUrl, actualsUrl, onStartAgain }) {
    const badge = statusBadge(batch.status);
    const inProgress = IN_PROGRESS_STATUSES.includes(batch.status);
    const finished = batch.status === 'loaded';

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
                <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
                    <div>
                        <h3 className="table__header">{$t('Upload status')}</h3>
                        <p className="tw-mt-1 tw-text-xs tw-text-charcoal-50">
                            {batch.file_name} · {batch.reference}
                        </p>
                    </div>
                    <Badge variant={badge.variant} size="md">
                        {badge.label}
                    </Badge>
                </div>

                {inProgress && (
                    <div className="tw-mt-4 tw-flex tw-items-center tw-gap-x-3 tw-rounded-lg tw-bg-blue-10 tw-px-4 tw-py-3" role="status">
                        <LoadingSpinner ySpacing={false} size="xs" />
                        <p className="tw-text-sm tw-text-charcoal-85">
                            {$t('Writing the upload. You can leave this page; we will email you when it has finished.')}
                        </p>
                    </div>
                )}

                {batch.status === 'failed' && (
                    <div className="tw-mt-4">
                        <Alert type="error">{batch.error_message || $t('The upload could not be written, and no transactions were changed.')}</Alert>
                    </div>
                )}

                {finished && (
                    <>
                        <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-5">
                            <Figure label={$t('Inserted')} value={batch.inserted} />
                            <Figure label={$t('Updated')} value={batch.updated} />
                            <Figure label={$t('Unchanged')} value={batch.unchanged} />
                            <Figure label={$t('Rejected')} value={batch.rejected} />
                            <Figure label={$t('Held')} value={batch.held} />
                        </div>
                        {batch.count_before !== null && (
                            <div className="tw-mt-3 tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
                                <Movement label={$t('Transactions')} before={batch.count_before} after={batch.count_after} format={formatCount} />
                                <Movement label={$t('Net value')} before={batch.net_value_before} after={batch.net_value_after} format={formatAmount} />
                            </div>
                        )}
                        {batch.rejected > 0 && (
                            <p className="tw-mt-3 tw-text-xs tw-text-charcoal-75">
                                {$t(
                                    'Rejected rows passed validation but no longer did when the upload was written, usually because a record they refer to changed in between.',
                                )}
                            </p>
                        )}
                        {batch.restored_archived > 0 && (
                            <p className="tw-mt-3 tw-text-xs tw-text-charcoal-75">
                                {batch.restored_archived === 1
                                    ? $t('1 transaction came back to the Archived tab.')
                                    : $t('{{number}} transactions came back to the Archived tab.', { number: formatCount(batch.restored_archived) })}
                            </p>
                        )}
                    </>
                )}
            </section>

            {finished && batch.reconciliation_changed_count > 0 && (
                <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
                    <h3 className="table__header">{$t('Reconciliation changed')}</h3>
                    <p className="tw-mt-1 tw-text-xs tw-text-charcoal-50">
                        {$t('{{count}} updated transactions changed their reconciliation. Review them in the Reconciliation tab.', {
                            count: formatCount(batch.reconciliation_changed_count),
                        })}
                    </p>
                    <div className="inbud-table_wrapper tw-mt-4">
                        <table className="inbud-table tw-w-full">
                            <thead>
                                <tr>
                                    <th className="tw-w-20 tw-text-end">{$t('Row')}</th>
                                    <th>{$t('Unique Document Reference')}</th>
                                    <th>{$t('Change')}</th>
                                    <th className="tw-text-end">{$t('Amount before')}</th>
                                    <th className="tw-text-end">{$t('Amount after')}</th>
                                    <th>{$t('Budget line code')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batch.reconciliation_changed.map((row) => (
                                    <tr key={`${row.row}-${row.document_reference}`}>
                                        <td className="tw-text-end">{row.row}</td>
                                        <td>{row.document_reference}</td>
                                        <td>{reconciliationChangeLabel(row.change)}</td>
                                        <td className="tw-text-end">{formatAmount(row.amount_before)}</td>
                                        <td className="tw-text-end">{formatAmount(row.amount_after)}</td>
                                        <td>
                                            {row.budget_code_before || '-'} <span aria-hidden="true">&rarr;</span> {row.budget_code_after || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {finished && batch.held_rows.length > 0 && (
                <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
                    <h3 className="table__header">{$t('Held rows')}</h3>
                    <p className="tw-mt-1 tw-text-xs tw-text-charcoal-50">
                        {$t('These rows were not written. Fix them in your file and upload the whole file again; rows already loaded will show as unchanged.')}
                    </p>
                    <div className="inbud-table_wrapper tw-mt-4">
                        <table className="inbud-table tw-w-full">
                            <thead>
                                <tr>
                                    <th className="tw-w-20 tw-text-end">{$t('Row')}</th>
                                    <th>{$t('Unique Document Reference')}</th>
                                    <th>{$t('Why')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batch.held_rows.map((row) => (
                                    <tr key={row.row}>
                                        <td className="tw-text-end">{row.row}</td>
                                        <td>{row.document_reference || '-'}</td>
                                        <td>
                                            {row.rejected && (
                                                <Badge variant="danger" className="tw-me-2">
                                                    {$t('Rejected')}
                                                </Badge>
                                            )}
                                            {row.issues.join('; ')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
                <Button as="a" variant="secondary" href={historyUrl}>
                    {$t('View upload history')}
                </Button>
                <Button as="a" variant="secondary" href={actualsUrl}>
                    {$t('Go to Actuals')}
                </Button>
                {!inProgress && (
                    <Button variant="primary" onClick={onStartAgain}>
                        {$t('Upload another file')}
                    </Button>
                )}
            </div>
        </div>
    );
}
